import type { ClientPositionView } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";

const ZERO = new BigDecimal(0);

/** Parses a USDv amount string into BigDecimal, treating null/invalid as 0. */
export function toUsdv(value: string | null | undefined): BigDecimal {
    if (value === null || value === undefined || value.trim() === "") return ZERO;
    try {
        const parsed = new BigDecimal(value);
        return parsed.isNaN() ? ZERO : parsed;
    } catch {
        return ZERO;
    }
}

/** Invalid or absent observations stay unknown, including LPs with legacy manual amounts. */
export function observedUsdv(value: string | null | undefined): BigDecimal | null {
    if (value === null || value === undefined || value.trim() === "") return null;
    try {
        const amount = new BigDecimal(value);
        return amount.isNaN() || amount.lt(0) ? null : amount;
    } catch {
        return null;
    }
}

export function formatObservedUsdv(amount: BigDecimal | null): string {
    return amount === null ? "—" : `$${amount.round(2).toFixed(2, true)}`;
}

export function isLpPosition(position: ClientPositionView): boolean {
    return Boolean(position.lpPosition) || /raydium|dex[_-]lp/i.test(position.programSurface.type);
}

export function lpPositionAmount(position: ClientPositionView): BigDecimal | null {
    if (!position.lpPosition || position.observation?.completeness !== "complete") return null;
    return observedUsdv(position.observedBalance);
}

export function comparePositionAmounts(a: BigDecimal | null, b: BigDecimal | null): number {
    if (a === null) return b === null ? 0 : -1;
    return b === null ? 1 : a.cmp(b);
}

/** One resolved inventory row contributes once; unavailable evidence blocks a complete total. */
export function sumPositionAmounts(amounts: Iterable<BigDecimal | null>): BigDecimal | null {
    let sum = new BigDecimal(0);
    for (const amount of amounts) {
        if (amount === null) return null;
        sum = sum.add(amount);
    }
    return sum;
}

/** Prefer the surface's APY; otherwise show the advertised USDv rate for Address and LP holdings. */
export function positionApyDisplay(position: ClientPositionView, advertisedApy: string): string {
    return position.programSurface.trailingApyPct ? `${position.programSurface.trailingApyPct}%` : advertisedApy;
}

export function hasLpPositions(positions: ClientPositionView[]): boolean {
    return positions.some(isLpPosition);
}

export interface ResolvedPortfolioPositionAmount {
    amount: BigDecimal | null;
    shouldFetchLiveUsdv: boolean;
}

/** Participation IDs identify program records, not additional physical principal.
 * The selected client inventory is already context scoped. Include the full LP
 * holding identity here so another program cannot add the same principal twice. */
export function portfolioHoldingIdentity(
    position: ClientPositionView,
    balance: ResolvedPortfolioPositionAmount | undefined
): string {
    const lp = position.lpPosition;
    if (lp) {
        return JSON.stringify([
            "lp",
            lp.holdingOwnerAddress,
            lp.protocol,
            lp.poolAddress,
            lp.canonicalPositionAddress ?? lp.positionAddress,
            lp.observedAssetMint
        ]);
    }
    if (balance?.shouldFetchLiveUsdv) {
        return JSON.stringify(["address", position.usdvBalanceAddress ?? position.account.address]);
    }
    return JSON.stringify(["position", position.uuid]);
}
